/**
 * Alcance de la papelera: quién ve (y vacía) qué.
 *
 * Regla acordada (2026-09-22, a partir de la reunión del 2026-09-18):
 *  - Cada usuario ve, restaura y vacía SOLO lo que él borró (`deletedBy`).
 *  - Solo root (`documents.root === true`: el responsable del sistema y el
 *    ingeniero) y la supervisora (`documents.supervisor === true`, desde el
 *    2026-09-23) ven y vacían la papelera global.
 *
 * Por qué `level: 'all'` ya no alcanza: las contadoras tienen ese nivel, y
 * con él veían lo que borraron todos los usuarios de todas las empresas —
 * y "Vaciar papelera" les borraba de forma permanente la papelera de todo el
 * sistema.
 *
 * Las reglas de Firestore exigen lo mismo (`match /trash`: isRoot(),
 * isSupervisor() o deletedBy == uid), así que esto no es solo un filtro de pantalla: una
 * consulta global de un no-root la rechaza el servidor.
 *
 * Módulo puro: sin React/Redux/Firestore, para poder testearlo directo.
 *
 * @param {object} params
 * @param {object} params.roles  roles del usuario (con .documents)
 * @param {string} params.uid    uid del usuario autenticado
 * @returns {{global:boolean, uid:string|null}|null}
 *   global → consulta sin filtro de autor
 *   !global → consulta con where('deletedBy', '==', uid)
 *   null → no hay nada que leer (sin root y sin uid)
 */
import { isGlobalSupervisor } from './closed_folder_perms';

export const trashScope = ({ roles, uid } = {}) => {
  // Root y supervisora ven la papelera global (lib/closed_folder_perms.js).
  if (isGlobalSupervisor(roles)) return { global: true, uid: uid || null };
  if (!uid) return null;
  return { global: false, uid };
};

/**
 * Une varias listas de entradas de papelera (p. ej. lo que borré yo y lo que
 * se borró por una solicitud mía aprobada): sin duplicados por `id` y de la
 * más reciente a la más antigua. Las sin `deletedAt` quedan al final.
 */
export const mergeTrashItems = (...lists) => {
  const byId = new Map();
  lists.forEach((list) => (list || []).forEach((it) => { if (!byId.has(it.id)) byId.set(it.id, it); }));
  return [...byId.values()].sort((a, b) => (b.deletedAt?.toMillis?.() || 0) - (a.deletedAt?.toMillis?.() || 0));
};

/**
 * ¿Puede restaurar o eliminar definitivamente esta entrada? El alcance
 * global sí; los demás, solo lo que ellos borraron. Lo que solo SOLICITARON
 * (lo borró una supervisora al aprobar) lo ven, no lo tocan.
 */
export const canRestoreTrashItem = ({ item, uid, scope } = {}) => {
  if (!scope || !uid) return false;
  if (scope.global) return true;
  return !!item && item.deletedBy === uid;
};
